import { AppBar, Tab, Tabs, useMediaQuery, useTheme } from '@material-ui/core'
import { useLocation } from 'react-router-dom'

import { getTranslate } from '../../localization'
import { LocalizedLink } from '../../routes/localizedRoutes'
import useStyles from './styles/index.styles'

/**
 * The peer views of promotions: the list, the aggregate Overview, and why promotions are
 * not applying.
 *
 * TABS ARE CORRECT HERE, AND THE EVALUATION TRACE IS STILL NOT ONE. `PromotionsHeader`
 * records why a tab strip was rejected for the trace: "tabs assert peer views of one subject,
 * and these are not peers — the list's subject is promotions; the trace's subject is a
 * BOOKING". The Overview passes that test, which is the whole justification for this
 * component: same subject, aggregated rather than listed. Evaluation Trace remains a
 * contextual action within Why not applied rather than a peer tab.
 *
 * NO ROLE FILTERING HERE, deliberately. `canAccessTab` (src/cache/auth.ts) filters by FEATURE
 * only — it reads `requiredFeatures` and nothing else — and the `rolesListWithReadAccess` on
 * tab entries is read by no call site anywhere. The conventions doc calls that out: config
 * that looks like a gate and is not is worse than no config. Both routes here carry the same
 * `promotionReaders` list and the same empty `requiredFeatures`, so a per-tab restriction
 * would be exactly that decorative gate. Access is enforced at the route, by
 * PromotionAccessGuard.
 *
 * A SEPARATE COMPONENT rather than a *Tabs wrapper screen. The sibling `*Tabs` screens own a
 * single route with a `:page` param and render the panel themselves; these are already
 * independent routes with their own guards, and restructuring them into one would change how
 * each is reached for no gain.
 *
 * RENDERED AS THE SECTION'S OWN NAVIGATION BAR, per portal-ux-conventions §4.3 and the
 * Notifications screen it points at: an AppBar wrapper, fullWidth on desktop, and NO visible
 * page title above it. Notifications states the reason directly — "an <h1> here is what made
 * this page read as a different product" — and SupportTabs and InventoryTabs render none
 * either. The promotion screens each rendered a prominent "Promotions" h3 above this strip,
 * which is what made the section look unlike the rest of the portal.
 *
 * scrollable below sm rather than fullWidth: three tabs divided equally at ~360px leaves the
 * labels unreadable, and the Persian ones run longer still.
 */
// Overview FIRST, and on the section's root path: it is the landing view of the Promotions
// menu. `/promotions/overview` still resolves, so existing links keep working; a path that
// matches no tab falls back to Overview below, which is the right answer for that alias.
//
// "Why not applied" replaces "Not applying". The old label was ambiguous in a way a tab
// cannot afford — not applying to WHAT, and in what tense — and this one echoes the screen's
// own heading, "Why promotions did not apply".
const PROMOTION_TABS = [
  { value: '/promotions', label: 'Overview', dataCy: 'PromotionsOverviewTab' },
  { value: '/promotions/list', label: 'List', dataCy: 'PromotionsListTab' },
  {
    value: '/promotions/skip-reasons',
    label: 'Why not applied',
    dataCy: 'PromotionsSkipReasonsTab'
  }
]

const PromotionTabs = () => {
  const { pathname } = useLocation()
  const classes = useStyles()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // The locale prefixes the path (localizedRoutes), so an exact match would never hit. Match
  // on the SUFFIX, longest first, so /promotions/overview is not swallowed by /promotions.
  const active =
    [...PROMOTION_TABS]
      .sort((a, b) => b.value.length - a.value.length)
      .find((tab) => pathname.endsWith(tab.value))?.value ?? '/promotions'

  return (
    <AppBar position="static" color="transparent" className={classes.root}>
      <Tabs
        value={active}
        indicatorColor="primary"
        textColor="primary"
        variant={isSmallScreen ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        aria-label={getTranslate('Promotion views')}>
        {PROMOTION_TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={getTranslate(tab.label)}
            className={classes.tab}
            data-cy={tab.dataCy}
            component={LocalizedLink}
            to={tab.value}
            disableRipple
          />
        ))}
      </Tabs>
    </AppBar>
  )
}

export default PromotionTabs
